<!-- =========================================================================================
    File Name: ChatNavbar.vue
    Description: Chat Application - Chat navbar
    ----------------------------------------------------------------------------------------
    Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
      Author: Pixinvent
    Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
	<div v-if="userId != null" class="chat__header">
		<vs-navbar class="p-4 flex navbar-custom" color="white" type="flat">
			<div class="relative flex mr-4">
				<feather-icon
					icon="MenuIcon"
					class="mr-4 cursor-pointer"
					v-if="isSidebarCollapsed"
					@click.stop="$emit('openSettingsSidebar')"
				/>
				<vs-avatar
					v-if="isGroup"
					class="m-0 border-2 border-solid border-white"
					icon-pack="feather"
					icon="icon-users"
					color="primary"
					size="40px"
				/>
				<vs-avatar
					v-else
					class="m-0 border-2 border-solid border-white"
					size="40px"
					:src="contactImage"
					@click.stop="$emit('showProfileSidebar', userId)"
				/>
				<div
					v-if="!isGroup"
					class="h-3 w-3 border-white border border-solid rounded-full absolute right-0 bottom-0"
					:class="'bg-' + getStatusColor"
				></div>
			</div>
			<div class="flex flex-col">
				<h6>{{ isGroup ? groupName : userDetails.username | capitalize }}</h6>
				<small style="font-size: 14px">
					{{ this.userFcmToken && this.userFcmToken.is_online ? $t("Chat.Online") : "" }}
				</small>
			</div>
			<vs-spacer></vs-spacer>
			<vs-dropdown vs-trigger-click class="ml-auto md:block hidden cursor-pointer">
				<feather-icon icon="MoreVerticalIcon" class="mr-4 cursor-pointer" />

				<vs-dropdown-menu class="w-64">
					<vs-dropdown-item class="mt-2">
						<div
							v-if="activeChatRoom.admin || isDefaultChat(activeChatRoom.key)"
							@click="$emit('openSettingsSidebar')"
							class="flex items-center"
						>
							<feather-icon icon="InfoIcon" class="inline-block mr-2" svgClasses="w-4 h-4" />
							<span>{{ $t("Chat.ViewGroupInfo") }}</span>
						</div>
					</vs-dropdown-item>
					<vs-dropdown-item>
						<div class="flex items-center" @click="activePrompt = true">
							<feather-icon icon="DownloadIcon" class="inline-block mr-2" svgClasses="w-4 h-4" />
							<span>{{ $t("ExportAll") }}</span>
						</div>
					</vs-dropdown-item>
					<vs-dropdown-item>
						<div class="flex items-center">
							<feather-icon icon="TrashIcon" class="inline-block mr-2" svgClasses="w-4 h-4" />
							<span>{{ $t("Chat.ClearHistory") }}</span>
						</div>
					</vs-dropdown-item>
					<vs-dropdown-item
						v-if="
							activeChatRoom.admin === userInfoId ||
							(!activeChatRoom.admin && !isDefaultChat(activeChatRoom.key))
						"
					>
						<div class="flex items-center text-danger" @click.stop="$emit('deleteChatEmit')">
							<feather-icon icon="Trash2Icon" class="inline-block mr-2" svgClasses="w-4 h-4" />
							<span>{{ $t("Crud.Delete") }} {{ $t("Chat.Chat") }}</span>
						</div>
					</vs-dropdown-item>
				</vs-dropdown-menu>
			</vs-dropdown>
		</vs-navbar>

		<vs-prompt
			:title="$t('ExportAll')"
			class="export-options"
			@cancel="clearFields"
			@accept="exportToExcel"
			:accept-text="$t('ExportAll')"
			@close="clearFields"
			:active.sync="activePrompt"
		>
			<vs-input v-model="fileName" placeholder="Enter File Name.." class="w-full" />
			<v-select v-model="selectedFormat" :options="formats" class="my-4" />
			<div class="flex">
				<span class="mr-4">{{ $t("MapModule.CellAutoWith") }}:</span>
				<vs-switch v-model="cellAutoWidth">{{ $t("MapModule.CellAutoWith") }}</vs-switch>
			</div>
		</vs-prompt>
	</div>
</template>

<script>
import {downloadFileFromFirebase} from "@/helpers/firebaseDB.js";
import Chat from "@/models/Chat.js";
import vSelect from "vue-select";

export default {
	components: {
		vSelect,
	},
	props: {
		userId: {
			type: Number,
			required: true,
		},
		userInfoId: {
			type: Number,
			required: true,
		},
		isPinnedProp: {
			type: Boolean,
			required: true,
		},
		isSidebarCollapsed: {
			type: Boolean,
			required: true,
		},
		isGroup: {
			type: Boolean,
			required: false,
			default: false,
		},
		groupName: {
			type: String,
			required: false,
			default: "",
		},
		activeChatRoom: {
			type: Chat,
			default: new Chat({}),
		},
	},
	data() {
		return {
			avatar: null,
			fileName: "",
			formats: ["xlsx", "csv", "txt"],
			cellAutoWidth: true,
			selectedFormat: "xlsx",
			headerTitle: ["time_stamp", "receiver", "sender", "text"],
			headerVal: ["time_stamp", "receiver", "sender", "text"],
			activePrompt: false,
		};
	},
	computed: {
		userDetails() {
			return this.$store.getters["chat/contact"](this.userId);
		},
		contactImage() {
			const contactImg = this.$store.getters["chat/contactImg"](this.userId);
			return contactImg ? contactImg.img : null;
		},
		userFcmToken() {
			return this.$store.getters["chat/contactFcmToken"](this.userId);
		},
		getStatusColor() {
			if (this.userFcmToken && this.userFcmToken.is_online) {
				return "success";
			} else {
				return "grey";
			}
		},
		isDefaultChat() {
			return (key) => key && key.split("_")[0] === "group";
		},
	},
	methods: {
		getUserStatus(isActiveUser) {
			return isActiveUser ? this.$store.state.AppActiveUser.status : this.userDetails.status;
		},
		getResourcePhoto() {
			const url = this.userDetails.img_url;
			if (!url) {
				this.avatar = null;
				return;
			}
			downloadFileFromFirebase(url)
				.then((res) => {
					this.avatar = res;
				})
				.catch((error) => {});
		},
		exportToExcel() {
			import("@/vendor/Export2Excel").then((excel) => {
				const list = Object.values(this.activeChatRoom.messages);
				const data = this.formatJson(this.headerVal, list);
				excel.export_json_to_excel({
					header: this.headerTitle,
					data,
					filename: this.fileName,
					autoWidth: this.cellAutoWidth,
					bookType: this.selectedFormat,
				});
				this.clearFields();
			});
		},
		formatJson(filterVal, jsonData) {
			return jsonData.map((v) =>
				filterVal.map((j) => {
					return v[j];
				})
			);
		},
		clearFields() {
			this.fileName = "";
			this.cellAutoWidth = true;
			this.selectedFormat = "xlsx";
		},
	},
	watch: {
		userId(first) {
			if (this.isGroup) {
				this.avatar = null;
				return;
			}
			this.getResourcePhoto();
		},
	},
};
</script>
